import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import {
  Button,
  Description,
  FieldError,
  Form,
  Heading,
  Input,
  InputGroup,
  Label,
  Link,
  Spinner,
  TextField,
} from '@heroui/react';
import { resetPassword, verifyResetCode } from '../../../lib/firebase';
import { showErrorFromUnknown, showSuccess } from '../../../lib/toast';
import { AsyncButton } from '../../../components/ui/async-button';
import { StatusCallout } from '../../../components/ui/status-callout';
import { AuthSplitLayout } from '../components/auth-split-layout';

function validatePassword(value: string): string | null {
  if (!value) return 'A senha é obrigatória';
  if (value.length < 6) return 'A senha deve ter pelo menos 6 caracteres';
  return null;
}

function validateConfirmPassword(value: string, password: string): string | null {
  if (!value) return 'Confirmação de senha é obrigatória';
  if (value !== password) return 'As senhas não coincidem';
  return null;
}

const EXPIRED_CODE_MESSAGE =
  'O código expirou ou é inválido. Solicite um novo email em "Esqueci a senha".';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [codeValid, setCodeValid] = useState(false);
  const [oobCode, setOobCode] = useState<string | null>(null);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ password?: string; confirmPassword?: string }>(
    {},
  );

  useEffect(() => {
    const code = searchParams.get('oobCode');
    if (!code) {
      setVerifying(false);
      setCodeValid(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        localStorage.removeItem('firebase:authUser');
        sessionStorage.clear();
        await verifyResetCode(code);
        if (!cancelled) {
          setOobCode(code);
          setCodeValid(true);
        }
      } catch {
        if (!cancelled) setCodeValid(false);
      } finally {
        if (!cancelled) setVerifying(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  useEffect(() => {
    if (!attemptedSubmit) return;
    setFieldErrors({
      password: validatePassword(password) ?? undefined,
      confirmPassword: validateConfirmPassword(confirmPassword, password) ?? undefined,
    });
  }, [password, confirmPassword, attemptedSubmit]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!oobCode || !codeValid || submitting) return;

    setAttemptedSubmit(true);
    const passwordError = validatePassword(password);
    const confirmError = validateConfirmPassword(confirmPassword, password);
    setFieldErrors({
      password: passwordError ?? undefined,
      confirmPassword: confirmError ?? undefined,
    });
    if (passwordError || confirmError) return;

    setSubmitting(true);
    try {
      await resetPassword(oobCode, password);
      showSuccess('Senha redefinida! Faça login com a nova senha.', 'Sucesso');
      navigate('/login', { replace: true });
    } catch (err) {
      showErrorFromUnknown(err, 'Erro ao redefinir senha');
      setCodeValid(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (verifying) {
    return (
      <AuthSplitLayout>
        <div className="flex flex-col items-center gap-4 py-12">
          <Spinner size="lg" />
          <Description>Verificando código...</Description>
        </div>
      </AuthSplitLayout>
    );
  }

  if (!codeValid) {
    return (
      <AuthSplitLayout>
        <div className="w-full max-w-sm space-y-6">
          <div className="flex w-full flex-col items-center space-y-1 text-center lg:items-start lg:text-left">
            <Heading level={2} className="w-full text-center text-2xl font-semibold lg:text-left">
              Link inválido
            </Heading>
            <Description className="text-center lg:text-left">{EXPIRED_CODE_MESSAGE}</Description>
          </div>
          <StatusCallout status="danger">{EXPIRED_CODE_MESSAGE}</StatusCallout>
          <Button variant="primary" size="lg" fullWidth onPress={() => navigate('/forgot-password')}>
            Solicitar novo email
          </Button>
          <p className="text-muted text-center text-sm lg:text-left">
            <Link className="text-sm font-medium text-accent" onPress={() => navigate('/login')}>
              Voltar ao login
            </Link>
          </p>
        </div>
      </AuthSplitLayout>
    );
  }

  return (
    <AuthSplitLayout>
      <div className="w-full max-w-sm space-y-6">
        <div className="flex w-full flex-col items-center space-y-1 text-center lg:items-start lg:text-left">
          <Heading level={2} className="w-full text-center text-2xl font-semibold lg:text-left">
            Nova senha
          </Heading>
          <Description className="text-center lg:text-left">
            Digite e confirme sua nova senha.
          </Description>
        </div>

        <Form className="flex flex-col gap-5" onSubmit={handleSubmit}>
          <TextField
            name="password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            isInvalid={attemptedSubmit && !!fieldErrors.password}
          >
            <Label>Nova senha</Label>
            <InputGroup fullWidth>
              <InputGroup.Input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                className="h-11 text-base"
              />
              <InputGroup.Suffix>
                <Button
                  isIconOnly
                  size="lg"
                  variant="ghost"
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  onPress={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </Button>
              </InputGroup.Suffix>
            </InputGroup>
            {attemptedSubmit && fieldErrors.password ? (
              <FieldError>{fieldErrors.password}</FieldError>
            ) : null}
          </TextField>

          <TextField
            name="confirmPassword"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            isInvalid={attemptedSubmit && !!fieldErrors.confirmPassword}
          >
            <Label>Confirmar nova senha</Label>
            <Input
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="new-password"
              className="h-11 text-base"
            />
            {attemptedSubmit && fieldErrors.confirmPassword ? (
              <FieldError>{fieldErrors.confirmPassword}</FieldError>
            ) : null}
          </TextField>

          <AsyncButton type="submit" variant="primary" size="lg" fullWidth isPending={submitting}>
            Redefinir senha
          </AsyncButton>
        </Form>

        <p className="text-muted text-center text-sm lg:text-left">
          Lembrou da senha?{' '}
          <Link className="text-sm font-medium text-accent" onPress={() => navigate('/login')}>
            Fazer login
          </Link>
        </p>
      </div>
    </AuthSplitLayout>
  );
}
